import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AutoAwesome as SparklesIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Description as ResumeIcon,
  Email as EmailIcon,
  Event as ScheduleIcon,
  ChatBubble as ChatBubbleIcon
} from '@mui/icons-material';

// Styled Components for the Widget
const WidgetContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 999999; /* Increased heavily to guarantee it stays above all other layout elements */
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;
`;

/* Fluid blob morphs organically inside the button */
const fluidFlow = keyframes`
  0%   { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; transform: translate(-18%, -18%); }
  20%  { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; transform: translate(12%, -22%); }
  40%  { border-radius: 50% 50% 25% 75% / 40% 55% 60% 50%; transform: translate(22%, 12%); }
  60%  { border-radius: 70% 30% 55% 45% / 60% 40% 45% 60%; transform: translate(-8%, 22%); }
  80%  { border-radius: 40% 60% 40% 60% / 50% 28% 72% 40%; transform: translate(-22%, 5%); }
  100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; transform: translate(-18%, -18%); }
`;

/* Wrapper — same size as button */
const ToggleButtonWrapper = styled.div`
  position: relative;
  width: 66px;
  height: 66px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
`;

/* Organic blob that flows inside the button circle */
const FluidBlob = styled.span`
  position: absolute;
  width: 80%;
  height: 80%;
  background: rgba(130, 118, 255, 0.6);
  animation: ${fluidFlow} 5s ease-in-out infinite;
  pointer-events: none;
  z-index: 0;
`;

const ToggleButton = styled(motion.button)`
  /* Vivid purple matching reference screenshots */
  background: linear-gradient(160deg, #6d66ff 0%, #4f46e5 60%, #3e38c4 100%);
  /* Matching purple outer ring */
  box-shadow:
    0 0 0 4px #3730a3,
    0 8px 28px rgba(79, 70, 229, 0.7);
  border: none;
  color: white;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  position: relative;
  z-index: 1;
  overflow: hidden; /* Clips the fluid blob inside the circle */
  transition: box-shadow 0.25s ease;

  /* Icon sits above the fluid blob */
  svg { position: relative; z-index: 2; }

  &:hover {
    box-shadow:
      0 0 0 5px #4338ca,
      0 10px 32px rgba(79, 70, 229, 0.85);
  }
`;


const ChatWindow = styled(motion.div)`
  width: clamp(350px, 45vw, 800px);
  height: 75vh;
  min-height: 550px;
  max-height: calc(100vh - 8rem);
  background: rgba(15, 23, 42, 0.75); /* Slate 900 with transparency for glass effect */
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1); /* Subtle crisp border */
  border-radius: 20px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent child content from leaking out of curved borders */
  box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.8), 0 0 40px rgba(56, 189, 248, 0.15);
  pointer-events: auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
`;

const ChatHeader = styled.div`
  display: flex;
  flex: 0 0 auto;
  min-height: 64px;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(to right, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.8));
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
`;

const Title = styled.div`
  color: #ffffff;
  font-size: 1.15rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: 0.5px;
`;

const CloseIconButton = styled.button`
  background: transparent;
  border: none;
  color: #f8fafc; /* Very light slate for max contrast */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  }
`;

const ChatBody = styled.div`
  flex: 1;
  min-height: 0; /* Crucial: Prevent intrinsic content from growing past flex container bounds */
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #475569;
    border-radius: 3px;
  }
`;

const MessageGroup = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const SenderLabel = styled.div`
  font-size: 0.8rem;
  color: #94a3b8;
  margin-bottom: 6px;
`;

const MessageBubble = styled.div`
  background: ${props => props.isUser ? 'linear-gradient(135deg, #0ea5e9, #0284c7)' : 'rgba(30, 41, 59, 0.8)'};
  backdrop-filter: ${props => props.isUser ? 'none' : 'blur(8px)'};
  color: ${props => props.isUser ? '#ffffff' : '#f8fafc'};
  padding: 14px 18px;
  border-radius: 14px;
  border-top-left-radius: ${props => props.isUser ? '14px' : '4px'};
  border-top-right-radius: ${props => props.isUser ? '4px' : '14px'};
  font-size: 0.95rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 85%;
  border: 1px solid ${props => props.isUser ? '#0ea5e9' : 'rgba(255, 255, 255, 0.05)'};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const QuickActionsArea = styled.div`
  padding: 10px 16px;
  border-top: 1px solid #334155;
  background: #0f172a;
`;

const SuggestedPrompts = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #334155;
    border-radius: 2px;
  }
`;

const PromptPill = styled.button`
  background: transparent;
  border: 1px solid #38bdf8;
  color: #38bdf8;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    background: rgba(56, 189, 248, 0.1);
  }
`;

const InputArea = styled.form`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: #1e293b;
  border-top: 1px solid #334155;
`;

const Input = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: #f8fafc;
  font-family: inherit;
  font-size: 1rem;
  outline: none;

  &::placeholder {
    color: #64748b;
  }
`;

const SendButton = styled.button`
  background: transparent;
  border: none;
  color: #38bdf8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover {
    color: #7dd3fc;
  }
`;

const LinkActionsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const ActionButton = styled.a`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #1e293b;
  border: 1px solid #334155;
  color: #e2e8f0;
  padding: 8px;
  border-radius: 8px;
  font-size: 0.75rem;
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    background: #334155;
    color: white;
  }
`;

const ProjectCard = styled(motion.div)`
  background: #1e293b;
  border: 1px solid #38bdf8;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:hover {
    background: rgba(56, 189, 248, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const ProjectTitle = styled.div`
  color: #38bdf8;
  font-weight: bold;
  font-size: 0.9rem;
`;

const ProjectDesc = styled.div`
  color: #94a3b8;
  font-size: 0.8rem;
  line-height: 1.4;
`;

const ProjectLink = styled.div`
  color: #0ea5e9;
  font-size: 0.75rem;
  margin-top: 4px;
  text-decoration: underline;
`;

const API_URL = window.location.hostname.includes('urvanshshah.netlify.app')
  ? 'https://portfolio-ai-backend-sr2m.onrender.com/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => window.crypto ? window.crypto.randomUUID() : Math.random().toString());

  const messagesEndRef = useRef(null);

  useEffect(() => {
    console.log(`🤖 AI Assistant initialized. Using API: ${API_URL}`);
    console.log(`🔑 Session ID: ${sessionId}`);
  }, [sessionId]);

  const suggestedPrompts = [
    "Years of experience?",
    "Best fit role?",
    "Tech stack",
    "ML projects",
    "Contact information"
  ];

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: 'welcome',
          sender: 'ai',
          text: '👋 Hi! I am Urvansh\'s AI assistant. Ask me about his skills, experience, or projects — or use a quick action below to connect directly.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    }
  }, [isOpen, messages]);

  const handleSendMessage = async (e, textOverride = null) => {
    e?.preventDefault();
    const textToSend = textOverride || inputValue;
    if (!textToSend.trim()) return;

    const newMsg = {
      id: Date.now().toString(),
      sender: 'user',
      text: textToSend,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, newMsg]);
    setInputValue('');
    setIsLoading(true);

    // AI placeholder
    const aiMsgId = (Date.now() + 1).toString();
    setMessages(prev => [
      ...prev,
      {
        id: aiMsgId,
        sender: 'ai',
        text: '...',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);

    try {
      let response;
      try {
        response = await fetch(`${API_URL}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: textToSend,
            sessionId: sessionId
          }),
        });
        if (!response.ok) throw new Error('Primary API error');
      } catch (primaryErr) {
        const FALLBACK_URL = 'https://portfolio-ai-backend-sr2m.onrender.com/api';
        if (API_URL !== FALLBACK_URL) {
          console.warn('Primary local API unreachable, falling back to live Render backend...', primaryErr);
          response = await fetch(`${FALLBACK_URL}/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              message: textToSend,
              sessionId: sessionId
            }),
          });
        } else {
          throw primaryErr;
        }
      }

      if (!response.ok) throw new Error('Network response was not ok');

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let done = false;
      let aiText = '';

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        const chunkValue = decoder.decode(value);
        if (chunkValue) {
          aiText += chunkValue;
          updateMessage(aiMsgId, aiText);
        }
      }
    } catch (error) {
      console.error('Error fetching chat:', error);
      updateMessage(aiMsgId, 'Sorry, I am having trouble connecting to my brain right now. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateMessage = (id, newText) => {
    setMessages(prev => prev.map(msg =>
      msg.id === id ? { ...msg, text: newText } : msg
    ));
  };

  const renderMessageContent = (text) => {
    // Regex for [PROJECT: Name | Desc | Link]
    const projectRegex = /\[PROJECT: (.*?) \| (.*?) \| (.*?)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = projectRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex, match.index)}</span>);
      }

      const [fullMatch, name, desc, link] = match;
      parts.push(
        <ProjectCard
          key={`project-${match.index}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => link !== 'n/a' && window.open(link, '_blank')}
        >
          <ProjectTitle>{name}</ProjectTitle>
          <ProjectDesc>{desc}</ProjectDesc>
          {link !== 'n/a' && <ProjectLink>View Project →</ProjectLink>}
        </ProjectCard>
      );
      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < text.length) {
      parts.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex)}</span>);
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <WidgetContainer>
      <AnimatePresence>
        {isOpen && (
          <ChatWindow
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            style={{ transformOrigin: 'bottom right' }}
          >
            <ChatHeader>
              <Title>
                <SparklesIcon fontSize="small" style={{ color: '#38bdf8' }} />
                Urvansh's AI Assistant
              </Title>
              <CloseIconButton onClick={() => setIsOpen(false)} title="Close Assistant">
                <CloseIcon fontSize="small" />
              </CloseIconButton>
            </ChatHeader>

            <ChatBody>
              {messages.map((msg) => (
                <MessageGroup
                  key={msg.id}
                  isUser={msg.sender === 'user'}
                  initial={{ opacity: 0, x: msg.sender === 'user' ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <SenderLabel>
                    {msg.sender === 'user' ? 'You' : 'AI Assistant'} • {msg.timestamp}
                  </SenderLabel>
                  <MessageBubble isUser={msg.sender === 'user'}>
                    {renderMessageContent(msg.text)}
                  </MessageBubble>
                </MessageGroup>
              ))}
              <div ref={messagesEndRef} />
            </ChatBody>

            <QuickActionsArea>
              <SuggestedPrompts>
                {suggestedPrompts.map((prompt, idx) => (
                  <PromptPill key={idx} onClick={(e) => handleSendMessage(e, prompt)} disabled={isLoading}>
                    {prompt}
                  </PromptPill>
                ))}
              </SuggestedPrompts>
              <LinkActionsRow>
                <ActionButton href="https://urvanshshah.netlify.app/Urvansh_Shah_Resume.pdf" target="_blank" rel="noopener noreferrer">
                  <ResumeIcon fontSize="small" /> Resume
                </ActionButton>
                <ActionButton href="mailto:urvanshshah9@gmail.com">
                  <EmailIcon fontSize="small" /> Email
                </ActionButton>
                <ActionButton href="https://calendly.com/urvanshshah9/30min" target="_blank" rel="noopener noreferrer">
                  <ScheduleIcon fontSize="small" /> Schedule
                </ActionButton>
              </LinkActionsRow>
            </QuickActionsArea>

            <InputArea onSubmit={handleSendMessage}>
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="ask me anything..."
                disabled={isLoading}
              />
              <SendButton type="submit" disabled={!inputValue.trim() || isLoading}>
                <SendIcon fontSize="small" />
              </SendButton>
            </InputArea>
          </ChatWindow>
        )}
      </AnimatePresence>

      <ToggleButtonWrapper>
        <ToggleButton
          onClick={() => setIsOpen(!isOpen)}
          title={isOpen ? 'Close Assistant' : 'Chat with AI'}
          whileTap={{ scale: 0.88 }}
        >
          {/* Fluid blob flows inside the button when closed */}
          {!isOpen && <FluidBlob />}
          {isOpen
            ? <CloseIcon style={{ fontSize: 22 }} />
            : <ChatBubbleIcon style={{ fontSize: 24 }} />}
        </ToggleButton>
      </ToggleButtonWrapper>

    </WidgetContainer>
  );
};

export default ChatWidget;
